import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  MessageBar,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FC } from 'react';
import { useForm } from 'react-hook-form';

import { Section, SectionContent, SectionItem, SectionTitle } from './atoms';
import { useControllers } from './hooks';
import { CampaignRejectionFormValues } from './types';
import { CampaignDetail } from '@/apis/core';

interface CampaignRejectionFormHUProps {
  campaign: CampaignDetail;
  onCancel?: () => void;
  onSubmit?: (explanation: string) => void;
}

const CampaignRejectionFormHU: FC<CampaignRejectionFormHUProps> = ({
  campaign,
  onCancel,
  onSubmit,
}) => {
  const {
    control,
    formState: { isValid, errors },
    handleSubmit,
  } = useForm<CampaignRejectionFormValues>({
    mode: 'onChange',
  });
  const { explanation, setExplanation, validateExplanation, explanationError } =
    useControllers(control);

  const handleValid = () => {
    onSubmit?.(explanation);
  };

  return (
    <form
      style={{ height: '100%' }}
      noValidate
      onSubmit={handleSubmit(handleValid)}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: 'xl',
          height: '100%',
          color: 't.black70',
          fontWeight: 400,
          lineHeight: 'normal',
        }}
      >
        <Flex
          sx={{
            flex: 1,
            flexDirection: 'column',
            gap: 'xl',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              color: 't.black90',
              fontWeight: 500,
              fontSize: '28px',
            }}
          >
            Reason for Rejection
          </Box>
          <Box sx={{ fontSize: 'H400' }}>
            Describe the reason for declining this messaging program. This
            feedback will be visible to all parties in the connectivity chain.
          </Box>
          <Section>
            <SectionTitle>Program Details</SectionTitle>
            <SectionContent columns={2}>
              <SectionItem title="Program ID">{campaign.uid}</SectionItem>
              <SectionItem title="Brand DBA Name">
                {campaign.brandName}
              </SectionItem>
              <SectionItem title="CSP">{campaign.cspUid}</SectionItem>
              <SectionItem title="Use Case">{campaign.usecase}</SectionItem>
            </SectionContent>
          </Section>

          <Box sx={{ fontSize: 'H300' }}>Indicates a required field*</Box>

          <TextInput readOnly label="Program ID" value={campaign.uid} />
          <TextInput readOnly label="CSP" value={campaign.cspUid} />
          <TextInput
            readOnly
            label="Brand DBA Name"
            value={campaign.brandName}
          />
          <TextInput
            required
            multiline
            error={!!explanationError?.message}
            minRows={3}
            maxRows={3}
            label="Detailed Explanation"
            note={explanationError?.message}
            value={explanation}
            onChange={setExplanation}
            onBlur={validateExplanation}
          />

          <MessageBar type="warning" showClose={false}>
            Declining this program will notify all parties in the connectivity
            chain and halt any pending activations for Sender ID
            {campaign.senderId}. This action will be logged.
          </MessageBar>
        </Flex>

        <Flex sx={{ justifyContent: 'flex-end', gap: 'l' }}>
          <Button shape="square" variant="outline" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button shape="square" size="lg" disabled={!isValid}>
            Reject
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default CampaignRejectionFormHU;
